import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { AdminApi } from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import Spacer from 'src/ui/atoms/spacer';
import Section from 'src/ui/atoms/section';
import { FileWithDocType } from 'src/ui/atoms/file-upload';
import Hint from 'src/ui/atoms/hint';

export interface AddMarketPricesModalFields {
  marketPrices: FileWithDocType;
}

export const Form = makeModalForm<AddMarketPricesModalFields>();

interface AddMarketPricesModalProps {
  productId: string;
  onAddMarketPrice: () => void;
  onClose: () => void;
}

const AddMarketPricesModal: FunctionComponent<AddMarketPricesModalProps> = ({
  productId,
  onAddMarketPrice,
  onClose,
}) => {
  const [csvHasErrors, setCsvHasErrors] = useState(false);

  const { makeAuthenticatedApi, withApi, error, loading } = useApiCall();

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const validateCsvContent = async (file: Blob): Promise<boolean> => {
    try {
      const text = await file.text();
      const lines = text.trim().split(/\r?\n/).filter(Boolean);

      if (lines.length === 0) return false;

      // skip header row if it exists
      const startIndex = lines[0].toLowerCase().startsWith('amount') ? 1 : 0;
      if (startIndex === 1 && lines.length === 1) return false;

      const dateTimeRegex = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:[.,]\d+)?\s*(?:Z|[+-]00(?::?00)?)$/;

      for (let i = startIndex; i < lines.length; i++) {
        const parts = lines[i].split(',');

        // must have exactly 4 columns: amount, decimals, currency, product_price_date_time
        if (parts.length !== 4) return false;

        const [amountStr, decimalsStr, currency, dateTime] = parts;
        const amount = Number(amountStr);

        // amount must be valid, not negative, and datetime must include seconds and timezone
        if (isNaN(amount) || amount < 0 || !decimalsStr || !currency || !dateTime || !dateTimeRegex.test(dateTime)) {
          return false;
        }
      }

      return true;
    } catch (err) {
      return false;
    }
  };

  const onSubmit = useCallback(
    async (fieldsValues: AddMarketPricesModalFields) => {
      if (!productId || !fieldsValues.marketPrices.file) return;

      setCsvHasErrors(false);

      const isValid = await validateCsvContent(fieldsValues.marketPrices.file);
      if (!isValid) {
        setCsvHasErrors(true);
        return;
      }

      await withApi(async () => {
        try {
          await api.adminProductPricesUploadCsvCreate({ file: fieldsValues.marketPrices.file!, product: productId });
          onAddMarketPrice();
        } catch (e) {
          setCsvHasErrors(true);
        }
      });
    },
    [productId, withApi],
  );

  return (
    <Modal onClose={onClose}>
      <Form i18nKey="distributionInformation" onSubmit={onSubmit} error={error}>
        <ModalHeader>
          <Translate name="marketPrices.addMarketPrices" />
        </ModalHeader>
        <ModalContent>
          <Section spacing="regular">
            <Translate name="marketPrices.addDescription1" as="p" />
            <strong>
              <Translate name="marketPrices.addDescription2" />
            </strong>
            <Spacer y={1} />
            <Translate name="marketPrices.addDescription3" />
            <Translate name="marketPrices.addDescription4" as="p" />
            <Translate name="marketPrices.addDescription5" />
          </Section>
          <strong>
            <Translate name="marketPrices.addDescription6" as="p" />
          </strong>
          <Form.Group name="marketPrices">
            <Form.FileUpload fileType="csv" />
          </Form.Group>

          {csvHasErrors && (
            <Hint variant="danger">
              <Translate name="marketPrices.csvError" />
            </Hint>
          )}
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'add',
                content: <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
                loading: loading,
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddMarketPricesModal;
